import { useMediaFile } from "@/OneLiners";
import { DocumentModel } from "../DocumentModel";
import { ActionResponse, ActionStatusHelper, DomainType, RecordMap, useAuditEnricher } from "@/Shared";

/**
 * =====================================================
 * Hook: useDocumentEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich Document domain data with related entities.
 * Combines users and media files into the DocumentModel for detailed views.
 *
 * Responsibilities:
 * - Fetch and maintain related data (users, media files)
 * - Track combined loading/error state
 * - Provide helper functions to enrich a single document, by ID, or a list
 * - Filter documents by resource type
 *
 * Notes:
 * - Returns a pure API for enrichment; no side effects beyond data fetching.
 * - Compatible with detail and list views for Documents.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export function useDocumentEnricher() {

    /**
             * Centralized audit enrichment hook
             * - Provides O(1) user resolution via precomputed Map
             * - Exposes audit enrichment helpers and status/error
             */
    const {
        enricherResponse: auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<DocumentModel>();

    const {
        dataMap: mediaFileMap,
        stateResponse: mediaFileStateResponse
    } = useMediaFile();

    /** =====================================================
     * Combined Loading & Error Status
     * =====================================================
     *
     * Combines status from all related hooks into a single ActionResponse:
     * - status: combined loading state (idle, loading, success, failed)
     * - error: first non-null error encountered
     */
    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            auditEnricherResponse.status,
            mediaFileStateResponse.status
        ),
        error: auditEnricherResponse.error ??
            mediaFileStateResponse.error
    };

    /** =====================================================
    * Public Helper Functions
    * =====================================================
    *
    * These functions enrich documents with:
    * - associated media
    * - audit info (created/updated by, timestamps)
    */

    /** Enrich an entire list of documents */
    const getEnrichedDocumentMap = (documentMap: RecordMap<DocumentModel>): RecordMap<DocumentModel> => {

        return documentMap.map<DocumentModel>(getStrictEnrichedDocument);
    }

    /** Enrich a single document */
    const getEnrichedDocument = (document?: DocumentModel): DocumentModel | undefined => {
        return document ? getStrictEnrichedDocument(document) : undefined;
    }

    const getStrictEnrichedDocument = (document: DocumentModel): DocumentModel => {
        return {
            ...getEnrichedAudit(document),
            media: document.mediaId ? mediaFileMap.get(document.mediaId) : undefined
        };
    }

    /** Filter documents by resource type and ID */
    const findByResource = (documentMap: RecordMap<DocumentModel>,
        domain: DomainType, resourceId: number) => {
        return documentMap
            .filter(doc => doc.documentCategory === domain && doc.resourceId === resourceId)
            .map(getStrictEnrichedDocument);
    }

    /** =====================================================
    * Return API
    * =====================================================
    *
    * Provides all enriched data and helper functions
    * for consuming components.
    */
    return {
        enricherResponse,
        findByResource,
        getEnrichedDocument,
        getStrictEnrichedDocument,
        getEnrichedDocumentMap,
    }
}